/**
 * Ordens de grandeza do projeto.
 *
 * Dados sintéticos com valores arbitrários fazem a tela parecer certa e a
 * avaliação errada: uma latência de dez milissegundos torna todo gráfico plano,
 * e ninguém percebe que a escala do eixo está inadequada para a faixa real.
 *
 * Os números abaixo vêm do dimensionamento em `plans/llm-server.md` e do cartão
 * do modelo. Alterá-los sem refazer aquele cálculo desalinha o protótipo do que
 * a bancada efetivamente mostrará.
 */

/** Alvo desejável de vazão por requisição, em tokens por segundo. */
export const TARGET_TOKENS_PER_SEC = 100;

/** Piso aceitável: ainda é cerca de três vezes a velocidade de leitura humana. */
export const FLOOR_TOKENS_PER_SEC = 60;

/** Teto de tempo até o primeiro token em carga conversacional, em milissegundos. */
export const TTFT_CEILING_MS = 1_000;

/** Latência por token equivalente ao alvo: 1000 ms ÷ 100 tokens. */
export const TARGET_TPOT_MS = 1_000 / TARGET_TOKENS_PER_SEC;

/** Latência por token equivalente ao piso: 1000 ms ÷ 60 tokens. */
export const FLOOR_TPOT_MS = 1_000 / FLOOR_TOKENS_PER_SEC;

/** Pesos do modelo em quantização FP8, em megabytes. */
export const MODEL_WEIGHTS_MB = 27 * 1024;

/**
 * Cache de estado por token, em bytes.
 * Vem de 2 × 16 camadas de atenção × 4 cabeças × 256 dimensões × 1 byte.
 */
export const KV_BYTES_PER_TOKEN = 32 * 1024;

/**
 * Estado recorrente por sessão, em megabytes.
 * Tamanho **fixo**: não cresce com o contexto, por vir das camadas recorrentes.
 */
export const RECURRENT_STATE_MB = 75;

/** Comprimento de contexto típico de uma conversa madura, em tokens. */
export const CHAT_CONTEXT_TOKENS = 8_192;

/** Taxa de aceitação da decodificação especulativa, medida em FP8. */
export const MTP_ACCEPT_RATE = 0.771;

/** Consumo nominal por acelerador, em watts, por modelo. */
export const GPU_POWER_W: Readonly<Record<string, number>> = {
  L40S: 350,
  "RTX 6000 Pro": 600,
  H100: 700,
  H200: 700,
  B200: 1_000,
};

/** Banda nominal por acelerador, em terabytes por segundo. */
export const GPU_BANDWIDTH_TBS: Readonly<Record<string, number>> = {
  L40S: 0.864,
  "RTX 6000 Pro": 1.8,
  H100: 3.35,
  H200: 4.8,
  B200: 8.0,
};

/** Custo por hora das instâncias de bancada, em dólares. */
export const INSTANCE_COST_USD_HOUR: Readonly<Record<string, number>> = {
  "g6e.2xlarge": 2.24,
  "g6e.12xlarge": 10.49,
  "g6e.48xlarge": 30.13,
  "p5.48xlarge": 55.04,
  "p6-b200.48xlarge": 113.93,
};

/**
 * Dispersão logarítmica da latência.
 *
 * Controla o peso da cauda à direita. O valor foi escolhido para produzir uma
 * separação nítida entre mediana e percentil 95 — que é justamente o que
 * justifica a regra de nunca apresentar média como métrica de latência.
 */
export const LATENCY_SIGMA = 0.35;

/** Dispersão maior, para cenários saturados, onde a cauda é mais pesada. */
export const LATENCY_SIGMA_SATURATED = 0.55;
